import type { JSX, ReactNode } from 'react';
import type { WireChatMessage } from '@logistics/contracts';
import { AnswerText } from '@/components/answer-text';
import { cn } from '@/lib/utils';
import { ArtifactCard } from './artifact-card';
import { Citations } from './citations';
import { useTypewriter } from './use-typewriter';

/** The three bouncing dots and what the agent is doing, from the stream's step events. */
export function Thinking({ text }: { text: string }): JSX.Element {
  return (
    <div className="text-mute flex items-center gap-2.5 py-1.5 text-[13px]" aria-live="polite">
      <span className="flex gap-1" aria-hidden>
        {[0, 0.15, 0.3].map((delay) => (
          <span
            key={delay}
            className="bg-acc size-[5px] rounded-full"
            style={{ animation: `dots 1.2s ease ${delay}s infinite` }}
          />
        ))}
      </span>
      {text}
    </div>
  );
}

/** The agent's side of the thread: the "AI" tile, then whatever is inside. */
export function AgentFrame({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex animate-[rise_.3s_ease_both] flex-col items-start gap-2">
      <div className="grid w-full max-w-[88%] grid-cols-[28px_minmax(0,1fr)] gap-3">
        <span className="bg-surf2 border-line text-acc mt-0.5 grid size-7 place-items-center rounded-[9px] border font-mono text-[10px] font-medium">
          AI
        </span>
        <div className="flex min-w-0 flex-col gap-2.5">{children}</div>
      </div>
    </div>
  );
}

const caret = (
  <span
    aria-hidden
    className="bg-acc ml-0.5 inline-block h-3.5 w-0.5 align-[-2px]"
    style={{ animation: 'caret .8s step-end infinite' }}
  />
);

/**
 * A stored answer. Just finished (`animate`), it is typed out, and only then
 * are its artifact card and citations shown and `onTyped` called — which is
 * when the page opens the artifact panel.
 */
export function AgentAnswer({
  message,
  animate,
  showing,
  onTyped,
  onOpenArtifact,
}: {
  message: WireChatMessage;
  animate: boolean;
  showing: boolean;
  onTyped: () => void;
  onOpenArtifact: () => void;
}): JSX.Element {
  const { shown, typing } = useTypewriter(message.text, animate, onTyped);
  return (
    <AgentFrame>
      <AnswerText text={shown} trailing={typing ? caret : undefined} />
      {!typing && message.artifact !== undefined && (
        <ArtifactCard artifact={message.artifact} showing={showing} onOpen={onOpenArtifact} />
      )}
      {!typing && message.citations.length > 0 && <Citations citations={message.citations} />}
    </AgentFrame>
  );
}

/**
 * What a question without an answer shows: the stream failed or was cut off,
 * or — read back later — the answer was never stored. "Try again" asks the
 * same question once more, where the person may still ask.
 */
export function AgentError({
  text,
  onRetry,
}: {
  text: string;
  onRetry?: (() => void) | undefined;
}): JSX.Element {
  return (
    <AgentFrame>
      <div
        role="alert"
        className={cn(
          'border-bad flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-xl border px-3.5 py-2.5 text-[13px] leading-normal',
          'bg-[color-mix(in_oklch,var(--bad)_8%,var(--surf))]',
        )}
      >
        <span className="min-w-0 [overflow-wrap:anywhere]">No answer — {text}.</span>
        {onRetry !== undefined && (
          <button
            type="button"
            onClick={onRetry}
            className="border-line text-ink hover:border-acc flex-none rounded-lg border bg-transparent px-2.5 py-1 text-[12.5px]"
          >
            Try again
          </button>
        )}
      </div>
    </AgentFrame>
  );
}
